import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Building, Users, Search, CreditCard, Calendar, Shield } from "lucide-react";

export default function HowItWorksPage() {
  return (
    <Layout>
      <div className="container py-6 md:py-12 px-4 md:px-8">
        <div className="max-w-3xl mx-auto text-center mb-8 md:mb-12">
          <h1 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4">How StayMiddle Works</h1>
          <p className="text-base md:text-xl text-muted-foreground leading-relaxed">
            Our platform connects property managers, rental agents, and travelers to create
            a seamless booking experience for luxury accommodations in Dubai.
          </p>
        </div>

        <div className="max-w-2xl mx-auto mb-8 md:mb-10">
          <div className="bg-white rounded-lg shadow p-4 md:p-6 flex flex-col items-center">
            <h2 className="text-base md:text-lg font-semibold mb-3 md:mb-2 text-center">🎧 Listen: How StayMiddle Works (Podcast)</h2>
            <audio controls className="w-full max-w-md">
              <source src="/staymiddle.mp4" type="audio/mp4" />
              Your browser does not support the audio element.
            </audio>
          </div>
        </div>

        <div className="space-y-12 md:space-y-16">
          {/* For Property Managers */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-center">
            <div className="order-2 md:order-1">
              <div className="inline-block p-3 bg-primary/10 rounded-lg mb-4">
                <Building className="w-6 h-6 md:w-8 md:h-8 text-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">For Property Managers</h2>
              <p className="text-base md:text-lg text-muted-foreground mb-4 md:mb-6 leading-relaxed">
                List your properties and manage bookings easily with our comprehensive platform built for Dubai's luxury rental market.
              </p>
              <ul className="space-y-3 md:space-y-4 mb-6">
                <li className="flex">
                  <div className="mr-3 md:mr-4 mt-1">
                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <Building className="h-3 w-3 text-primary" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-sm md:text-base">Easy Property Listings</h3>
                    <p className="text-muted-foreground text-sm md:text-base">Create and manage property listings with detailed information, photos, and pricing.</p>
                  </div>
                </li>
                <li className="flex">
                  <div className="mr-3 md:mr-4 mt-1">
                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <Calendar className="h-3 w-3 text-primary" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-sm md:text-base">Calendar Syncing</h3>
                    <p className="text-muted-foreground text-sm md:text-base">Synchronize with your Airbnb calendar to prevent double bookings and manage availability.</p>
                  </div>
                </li>
                <li className="flex">
                  <div className="mr-3 md:mr-4 mt-1">
                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <CreditCard className="h-3 w-3 text-primary" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-sm md:text-base">Direct Payments</h3>
                    <p className="text-muted-foreground text-sm md:text-base">Receive payments directly to your bank account through our secure Stripe Connect integration.</p>
                  </div>
                </li>
              </ul>
              <Button 
                onClick={() => window.location.href = "/auth?tab=register&role=manager"}
                className="bg-primary hover:bg-primary/90 w-full md:w-auto"
                size="lg"
              >
                Register as Property Manager
              </Button>
            </div>
            <div className="order-1 md:order-2 relative h-[200px] md:h-[400px] rounded-lg overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1612831819784-9084c50c5477?q=80&w=3870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" 
                alt="Property manager working" 
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </section>

          {/* Detailed Audio Podcast Section */}
          <div className="max-w-2xl mx-auto mb-8 md:mb-10">
            <div className="bg-white rounded-lg shadow p-4 md:p-6 flex flex-col items-center">
              <h2 className="text-base md:text-lg font-semibold mb-3 md:mb-2 text-center">🎧 Listen: StayMiddle In-Depth (Podcast)</h2>
              <audio controls className="w-full max-w-md">
                <source src="/staymiddle-details.mp4" type="audio/mp4" />
                Your browser does not support the audio element.
              </audio>
            </div>
          </div>

          {/* For Rental Agents */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-center">
            <div className="order-1 relative h-[200px] md:h-[400px] rounded-lg overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1707009504180-734389582aa3?q=80&w=3621&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" 
                alt="Real estate agent with client" 
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="order-2">
              <div className="inline-block p-3 bg-primary/10 rounded-lg mb-4">
                <Users className="w-6 h-6 md:w-8 md:h-8 text-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">For Rental Agents</h2>
              <p className="text-base md:text-lg text-muted-foreground mb-4 md:mb-6 leading-relaxed">
                Earn commissions by connecting travelers with premium properties in Dubai without the hassle of property management.
              </p>
              <ul className="space-y-3 md:space-y-4 mb-6">
                <li className="flex">
                  <div className="mr-3 md:mr-4 mt-1">
                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-3 w-3 text-primary"
                      >
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-sm md:text-base">Generate Booking Links</h3>
                    <p className="text-muted-foreground text-sm md:text-base">Create unique booking links for properties to track your referrals.</p>
                  </div>
                </li>
                <li className="flex">
                  <div className="mr-3 md:mr-4 mt-1">
                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <CreditCard className="h-3 w-3 text-primary" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-sm md:text-base">Commission Tracking</h3>
                    <p className="text-muted-foreground text-sm md:text-base">Easily track and withdraw your earned commissions through our transparent system.</p>
                  </div>
                </li>
                <li className="flex">
                  <div className="mr-3 md:mr-4 mt-1">
                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <CreditCard className="h-3 w-3 text-primary" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-sm md:text-base">Custom Markups</h3>
                    <p className="text-muted-foreground text-sm md:text-base">Set your own markups on properties to maximize your earnings potential.</p>
                  </div>
                </li>
              </ul>
              <Button 
                onClick={() => window.location.href = "/auth?tab=register&role=agent"}
                className="bg-primary hover:bg-primary/90 w-full md:w-auto"
                size="lg"
              >
                Register as Rental Agent
              </Button>
            </div>
          </section>
        </div>

        {/* Call to Action */}
        <div className="mt-12 md:mt-16 bg-muted rounded-lg p-6 md:p-8 text-center">
          <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Ready to Get Started?</h2>
          <p className="text-base md:text-lg text-muted-foreground mb-6 max-w-2xl mx-auto leading-relaxed">
            Join StayMiddle today and be part of Dubai's premium rental marketplace that connects property managers, rental agents, and travelers.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 md:gap-4">
            <Button 
              onClick={() => window.location.href = "/auth?tab=register&role=manager"}
              className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
              size="lg"
            >
              <Building className="mr-2 h-4 w-4" />
              Register as Property Manager
            </Button>
            <Button 
              onClick={() => window.location.href = "/auth?tab=register&role=agent"}
              className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
              size="lg"
            >
              <Users className="mr-2 h-4 w-4" />
              Register as Rental Agent
            </Button>
            <Button 
              onClick={() => window.location.href = "/properties"}
              variant="outline"
              className="w-full sm:w-auto"
              size="lg"
            >
              <Search className="mr-2 h-4 w-4" />
              Explore Properties
            </Button>
          </div>
        </div>

        {/* Social Media Section */}
        <div className="mt-8 md:mt-12 text-center">
          <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-2">Follow Us</h3>
          <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4">
            <a href="https://twitter.com/StayMiddleApp" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm md:text-base">Twitter @StayMiddleApp</a>
            <a href="https://instagram.com/StayMiddleApp" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm md:text-base">Instagram @StayMiddleApp</a>
            <a href="https://facebook.com/StayMiddleApp" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm md:text-base">Facebook @StayMiddleApp</a>
          </div>
        </div>
      </div>
    </Layout>
  );
}